/**
 * Offline cache coherence for multi-instance Power Distribution (#996).
 * Keeps request.powerDistributionConfigurations aligned with the power distribution
 * API / React Query. Mirrors largeUpsOfflineSync.
 */

import {
  offlineCache,
  type OfflinePowerDistributionOperation,
} from './offlineCache';
import { applyPendingRepeatableOperations } from './repeatableSectionOfflineSync';
import { groupPowerDistributionByStatus } from '../components/powerdistribution/powerDistributionListUtils';

import type {
  GroupedPowerDistributionConfigurations,
  PowerDistributionConfiguration,
} from '../types/powerDistribution';

/** Power distribution list from cached request detail (offline reads). */
export async function getCachedPowerDistributionList(
  requestId: string,
): Promise<PowerDistributionConfiguration[]> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  return cachedRequest?.powerDistributionConfigurations ?? [];
}

/** Grouped units derived from cached list (same shape as GET …/power-distribution/grouped). */
export async function getCachedPowerDistributionGrouped(
  requestId: string,
): Promise<GroupedPowerDistributionConfigurations> {
  return groupPowerDistributionByStatus(
    await getCachedPowerDistributionList(requestId),
  );
}

/** Single unit from cached list when offline. */
export async function getCachedPowerDistributionById(
  requestId: string,
  powerDistributionId: number,
): Promise<PowerDistributionConfiguration | undefined> {
  return (await getCachedPowerDistributionList(requestId)).find(
    unit => unit.id === powerDistributionId,
  );
}

/**
 * After a successful online power distribution fetch or mutation refetch, mirror units
 * onto the cached request so offline reads stay consistent.
 */
export async function mirrorPowerDistributionToOfflineRequestCache(
  requestId: string,
  powerDistribution: PowerDistributionConfiguration[],
): Promise<void> {
  const cachedRequest = await offlineCache.getCachedRequestById(requestId);
  if (!cachedRequest) {
    return;
  }
  await offlineCache.updateCachedRequestPowerDistribution(
    requestId,
    powerDistribution,
  );
}

/** Pending offline power distribution queue entries for one request (create/update/delete/reorder). */
export async function getPendingPowerDistributionOpsForRequest(
  requestId: string,
): Promise<
  Awaited<ReturnType<typeof offlineCache.getPendingPowerDistributionOperations>>
> {
  const pending = await offlineCache.getPendingPowerDistributionOperations();
  return pending.filter(op => String(op.requestId) === String(requestId));
}

/**
 * In-flight offline power distribution ops (status `pending` OR `syncing`) for one request.
 * Used by the ONLINE overlay: the sync flips an op to `syncing` BEFORE its POST,
 * so a `pending`-only view drops an op mid-sync and the item vanishes during the
 * reconnect race. Including `syncing` keeps it visible until the op is dequeued.
 */
export async function getInFlightPowerDistributionOpsForRequest(
  requestId: string,
): Promise<OfflinePowerDistributionOperation[]> {
  const queue = await offlineCache.getOfflinePowerDistributionQueue();
  return queue.filter(
    op =>
      (op.status === 'pending' || op.status === 'syncing') &&
      String(op.requestId) === String(requestId),
  );
}

/**
 * Overlay pending offline power distribution operations onto a base list so offline reads
 * reflect not-yet-synced changes. Pure and deterministic (ops applied in
 * timestamp order). De-dupes creates by id so an optimistic unit already written
 * into the base list is not duplicated.
 */
export function applyPendingPowerDistributionOperations(
  requestId: string,
  base: PowerDistributionConfiguration[],
  ops: OfflinePowerDistributionOperation[],
): PowerDistributionConfiguration[] {
  return applyPendingRepeatableOperations(requestId, base, ops, {
    instanceId: op => op.powerDistributionId,
    reorderIds: op => op.powerDistributionIds,
  });
}

/** Cached power distribution list with pending offline operations overlaid (offline reads). */
export async function getOfflinePowerDistributionList(
  requestId: string,
): Promise<PowerDistributionConfiguration[]> {
  const [base, ops] = await Promise.all([
    getCachedPowerDistributionList(requestId),
    getPendingPowerDistributionOpsForRequest(requestId),
  ]);
  return applyPendingPowerDistributionOperations(requestId, base, ops);
}

/** Grouped units (existing/new) from the overlaid offline list. */
export async function getOfflinePowerDistributionGrouped(
  requestId: string,
): Promise<GroupedPowerDistributionConfigurations> {
  return groupPowerDistributionByStatus(
    await getOfflinePowerDistributionList(requestId),
  );
}

/** Single unit from the overlaid offline list (includes pending creates). */
export async function getOfflinePowerDistributionById(
  requestId: string,
  powerDistributionId: number,
): Promise<PowerDistributionConfiguration | undefined> {
  return (await getOfflinePowerDistributionList(requestId)).find(
    unit => unit.id === powerDistributionId,
  );
}
